import React, { useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Button,
  alpha,
  useTheme,
  Chip,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import {
  Close,
  Add,
  Remove,
  LocalOffer,
  ShoppingCart,
  AddShoppingCart,
  CheckCircle,
  ReceiptLong,
  PlaylistAdd,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
import ImageLoader from "../../../../components/ImageLoader/ImageLoader";
import OfferService from "../../../../services/OfferService";
import FrequentlyBoughtTogetherService from "../../../../services/FrequentlyBoughtTogetherService";
import {
  groupModifiersByParent,
  calculateItemTotalPrice,
  calculateUnitPrice,
  validateRequiredModifiers,
  toggleModifier,
} from "./customizationUtils";
import { CART_SUMMARY_WIDTH } from "./CartSummary";

const customizationOverlaySx = {
  pointerEvents: "none",
  "& .MuiDialog-container": {
    justifyContent: "flex-start",
    alignItems: "stretch",
  },
  "& .MuiDialog-paper": {
    pointerEvents: "auto",
    width: `calc(100% - ${CART_SUMMARY_WIDTH}px)`,
    maxWidth: "none",
    height: "100%",
    maxHeight: "none",
    m: 0,
    borderRadius: 0,
  },
  "& .MuiBackdrop-root": {
    right: CART_SUMMARY_WIDTH,
    width: `calc(100% - ${CART_SUMMARY_WIDTH}px)`,
  },
};

/**
 * ItemCustomizationDialog Component
 * Overlay for customizing menu items; leaves the cart column visible on the right
 */
const ItemCustomizationDialog = ({
  open,
  item,
  onClose,
  onAddToCart,
  allMenuItems = [],
  onAddMenuItem,
  onAddOfferToCart,
  cartItemCount = 0,
  onGoToCart,
}) => {
  const theme = useTheme();
  const [selectedVariation, setSelectedVariation] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [offers, setOffers] = useState([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [relatedAddedIds, setRelatedAddedIds] = useState({});
  const [currentOfferIndex, setCurrentOfferIndex] = useState(0);
  const [isOffersDialogOpen, setIsOffersDialogOpen] = useState(false);
  const [fbtItems, setFbtItems] = useState([]);
  const [loadingFbt, setLoadingFbt] = useState(false);
  const [fbtIndex, setFbtIndex] = useState(0);

  // Reset state when dialog opens with new item
  React.useEffect(() => {
    if (open && item) {
      // Pre-select first variation if available
      if (item.variations && item.variations.length > 0) {
        setSelectedVariation(item.variations[0].id);
      }
      setSelectedModifiers({});
      setQuantity(1);
      setRelatedAddedIds({});
      setCurrentOfferIndex(0);
      setIsOffersDialogOpen(false);
      setFbtItems([]);
      setFbtIndex(0);
    }
  }, [open, item]);

  // Fetch FBT items from API
  React.useEffect(() => {
    const fetchFbt = async () => {
      if (!open || !item?.id) return;
      try {
        setLoadingFbt(true);
        const response = await FrequentlyBoughtTogetherService.getAll({
          menuItemId: item.id,
          limit: 8,
        });
        const data = response?.data?.view?.data || response?.data;
        const items = data?.items || [];
        setFbtItems(items);
      } catch (error) {
        console.error("Error fetching FBT items:", error);
        setFbtItems([]);
      } finally {
        setLoadingFbt(false);
      }
    };
    fetchFbt();
  }, [open, item?.id]);

  React.useEffect(() => {
    if (currentOfferIndex > 0 && currentOfferIndex >= offers.length) {
      setCurrentOfferIndex(Math.max(0, offers.length - 1));
    }
  }, [offers, currentOfferIndex]);

  // Fetch offers
  React.useEffect(() => {
    const fetchOffers = async () => {
      if (!open || !item?.id) return;

      try {
        setLoadingOffers(true);
        const response = await OfferService.getAll({
          isActive: true,
          menuItemId: item.id,
        });

        console.log("Offers", response);

        if (response?.data?.view?.data) {
          // Filter active offers that are within date range
          const now = new Date();
          const activeOffers = response.data.view.data.filter((offer) => {
            const startDate = new Date(offer.startDate);
            const endDate = new Date(offer.endDate);

            if (
              Number.isNaN(startDate.getTime()) ||
              Number.isNaN(endDate.getTime())
            ) {
              return offer.isActive;
            }

            return offer.isActive && now >= startDate && now <= endDate;
          });
          // Only show offers that contain the current menu item
          const filtered = activeOffers.filter((offer) =>
            (offer.OfferItems || []).some((oi) => oi.menuItemId === item.id)
          );
          setOffers(filtered);
        }
      } catch (error) {
        console.error("Error fetching offers:", error);
      } finally {
        setLoadingOffers(false);
      }
    };

    fetchOffers();
  }, [open, item?.id]);

  const getDisplayPrice = (menuItem) => {
    if (menuItem.variations && menuItem.variations.length > 0) {
      const sortedVariations = [...menuItem.variations].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
      );
      return parseFloat(sortedVariations[0]?.price || 0);
    }
    return parseFloat(menuItem.price || 0);
  };

  // Normalise API FBT items into a shape the card UI can consume
  const frequentlyBoughtTogether = React.useMemo(() => {
    return fbtItems.map((apiItem) => ({
      id: apiItem.menuItemId,
      name: apiItem.menuItemName,
      image: apiItem.image,
      categoryName: apiItem.categoryName,
      price: apiItem.basePrice ?? 0,
      variations: [],
      modifierOptions: [],
    }));
  }, [fbtItems]);

  const modifierGroups = groupModifiersByParent(item?.modifierOptions);

  const handleModifierToggle = (
    modifierId,
    optionId,
    isRequired,
    maxSelections,
  ) => {
    setSelectedModifiers((prev) =>
      toggleModifier({
        currentSelections: prev,
        modifierId,
        optionId,
        isRequired,
        maxSelections,
      }),
    );
  };

  const calculateTotalPrice = () => {
    return calculateItemTotalPrice({
      item,
      selectedVariation,
      selectedModifiers,
      modifierGroups,
      quantity,
    });
  };

  const buildCustomizedItem = () => {
    return {
      ...item,
      selectedVariation: selectedVariation
        ? item.variations.find((v) => v.id === selectedVariation)
        : null,
      selectedModifiers: Object.entries(selectedModifiers).flatMap(
        ([modifierId, optionIds]) => {
          const group = modifierGroups[modifierId];
          return optionIds.map((optionId) =>
            group.options.find((o) => o.id === optionId),
          );
        },
      ),
      quantity,
      finalPrice: calculateUnitPrice({
        item,
        selectedVariation,
        selectedModifiers,
        modifierGroups,
      }),
    };
  };

  const resetSelections = () => {
    // Reset to first variation if available
    if (item.variations && item.variations.length > 0) {
      setSelectedVariation(item.variations[0].id);
    }
    setSelectedModifiers({});
    setQuantity(1);
  };

  const handleAddToCart = () => {
    onAddToCart(buildCustomizedItem());
    onClose();
  };

  const handleAddAnother = () => {
    onAddToCart(buildCustomizedItem());
    resetSelections();
  };

  const canAddToCart = () => {
    return validateRequiredModifiers(modifierGroups, selectedModifiers);
  };

  const handleAddRelatedItem = (relatedItem) => {
    if (!onAddMenuItem) return;

    onAddMenuItem(relatedItem);

    const hasVariations =
      relatedItem.variations && relatedItem.variations.length > 0;
    const hasModifiers =
      relatedItem.modifierOptions && relatedItem.modifierOptions.length > 0;

    if (!hasVariations && !hasModifiers) {
      setRelatedAddedIds((prev) => ({ ...prev, [relatedItem.id]: true }));
    }
  };

  const handleOpenOffersDialog = () => {
    if (!offers.length) return;
    setIsOffersDialogOpen(true);
  };

  const handleAddOfferFromDialog = (offer) => {
    if (!onAddOfferToCart) return;
    onAddOfferToCart(offer);
  };

  const handleGoToCartFromOffers = () => {
    setIsOffersDialogOpen(false);
    if (onGoToCart) {
      onGoToCart();
      return;
    }
    onClose();
  };

  if (!item) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      sx={customizationOverlaySx}
      PaperProps={{
        sx: {
          bgcolor: "#fafafa",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header with Close Button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 2.5,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              right: 0,
              width: "300px",
              height: "300px",
              borderRadius: "50%",
              background: alpha("#fff", 0.05),
              transform: "translate(30%, -30%)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              width: "200px",
              height: "200px",
              borderRadius: "50%",
              background: alpha("#fff", 0.03),
              transform: "translate(-30%, 30%)",
            },
          }}
        >
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                textShadow: `0 2px 12px ${alpha("#000", 0.2)}`,
              }}
            >
              Customize Your Order
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              position: "relative",
              zIndex: 1,
              color: "white",
              bgcolor: alpha("#fff", 0.15),
              backdropFilter: "blur(10px)",
              border: `2px solid ${alpha("#fff", 0.2)}`,
              "&:hover": {
                bgcolor: alpha("#fff", 0.25),
              },
            }}
          >
            <Close fontSize="large" />
          </IconButton>
        </Box>

        {/* Main Content - Split Layout */}
        <Box
          sx={{
            flex: 1,
            overflow: "auto",
            display: "flex",
            gap: 0,
          }}
        >
          {/* Left Side - Image & Info */}
          <Box
            sx={{
              width: 380,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              p: 3,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            {/* Image */}
            <Paper
              elevation={3}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                height: 280,
              }}
            >
              {item.image ? (
                <ImageLoader
                  imagePath={item.image}
                  width="100%"
                  height={280}
                  alt={item.name}
                />
              ) : (
                <Box
                  sx={{
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <Typography
                    variant="h1"
                    sx={{ opacity: 0.2, fontWeight: 700 }}
                  >
                    {item.name.charAt(0)}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Item Details */}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                {item.name}
              </Typography>
              {item.description && (
                <Typography variant="body1" color="text.secondary">
                  {item.description}
                </Typography>
              )}
            </Box>

            {/* Quantity Selector */}
            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
              <Typography
                variant="h6"
                sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
              >
                Quantity
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <IconButton
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  disabled={quantity <= 1}
                  size="large"
                  sx={{
                    border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                    width: 50,
                    height: 50,
                    "&:disabled": {
                      opacity: 0.3,
                    },
                  }}
                >
                  <Remove fontSize="large" />
                </IconButton>
                <Typography
                  variant="h3"
                  sx={{
                    minWidth: 60,
                    textAlign: "center",
                    fontWeight: 800,
                  }}
                >
                  {quantity}
                </Typography>
                <IconButton
                  onClick={() => setQuantity(quantity + 1)}
                  size="large"
                  sx={{
                    border: `2px solid ${alpha(theme.palette.divider, 0.3)}`,
                    width: 50,
                    height: 50,
                  }}
                >
                  <Add fontSize="large" />
                </IconButton>
              </Box>
            </Paper>

            {/* Price Summary */}
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Total Price
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: theme.palette.primary.main,
                }}
              >
                Rs. {calculateTotalPrice().toFixed(2)}
              </Typography>
            </Paper>
          </Box>

          {/* Middle - Options (Scrollable) */}
          <Box
            sx={{
              flex: 1,
              p: 3,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minWidth: 0,
              minHeight: 0,
              gap: 2,
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: "auto",
                pr: 0.5,
              }}
            >
              {/* Variations */}
              {item.variations && item.variations.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Select Size
                    </Typography>
                    <Chip
                      label="Required"
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        fontSize: "0.75rem",
                        bgcolor: theme.palette.error.main,
                        color: "white",
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.5 }}
                  >
                    Tap a size to continue. Larger cards are optimized for POS touch
                    screens.
                  </Typography>
                  <Grid container spacing={1.25}>
                    {[...item.variations]
                      .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                      .map((variation) => {
                        const isSelected = selectedVariation === variation.id;

                        return (
                          <Grid
                            key={variation.id}
                            size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                          >
                            <Paper
                              onClick={() => setSelectedVariation(variation.id)}
                              elevation={0}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                border: `2px solid ${isSelected ? theme.palette.primary.main : alpha(theme.palette.divider, 0.6)}`,
                                bgcolor: isSelected
                                  ? alpha(theme.palette.primary.main, 0.08)
                                  : "white",
                                cursor: "pointer",
                                transition: "all 0.2s ease",
                                minHeight: 74,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                "&:hover": {
                                  borderColor: theme.palette.primary.main,
                                  boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.16)}`,
                                  transform: "translateY(-1px)",
                                },
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 800, lineHeight: 1.15 }}
                                >
                                  {variation.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mt: 0.25,
                                    fontWeight: 700,
                                    color: theme.palette.primary.main,
                                  }}
                                >
                                  +Rs. {parseFloat(variation.price).toFixed(2)}
                                </Typography>
                              </Box>
                              <CheckCircle
                                sx={{
                                  fontSize: 22,
                                  color: isSelected
                                    ? theme.palette.primary.main
                                    : alpha(theme.palette.text.secondary, 0.28),
                                }}
                              />
                            </Paper>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Box>
              )}

              {/* Modifiers */}
              {Object.entries(modifierGroups).map(([modifierId, group]) => {
                const isRequired = group.modifier.isRequired;
                const maxSelections = group.modifier.maxSelections;
                const isSingleSelection =
                  maxSelections === 1 || maxSelections === null;

                return (
                  <Box key={modifierId} sx={{ mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1.5,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                        {group.modifier.name}
                      </Typography>
                      {isRequired && (
                        <Chip
                          label="Required"
                          size="small"
                          sx={{
                            height: 24,
                            fontWeight: 700,
                            fontSize: "0.75rem",
                            bgcolor: theme.palette.error.main,
                            color: "white",
                            "& .MuiChip-label": {
                              px: 1,
                            },
                          }}
                        />
                      )}
                      {!isSingleSelection && maxSelections !== null && (
                        <Chip
                          label={`Up to ${maxSelections}`}
                          size="small"
                          sx={{
                            height: 24,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            bgcolor: alpha(theme.palette.info.main, 0.15),
                            color: theme.palette.info.dark,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                          }}
                        />
                      )}
                    </Box>
                    {group.modifier.description && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mb: 1.5, display: "block" }}
                      >
                        {group.modifier.description}
                      </Typography>
                    )}

                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {group.options.map((option) => {
                        const isSelected = (
                          selectedModifiers[modifierId] || []
                        ).includes(option.id);

                        return (
                          <Chip
                            key={option.id}
                            label={`${option.name} ${option.price > 0 ? `(+Rs. ${parseFloat(option.price).toFixed(2)})` : ""}`}
                            onClick={() =>
                              handleModifierToggle(
                                modifierId,
                                option.id,
                                isRequired,
                                maxSelections,
                              )
                            }
                            variant={isSelected ? "filled" : "outlined"}
                            sx={{
                              height: 36,
                              fontSize: "0.85rem",
                              fontWeight: 600,
                              borderRadius: 2,
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              bgcolor: isSelected
                                ? theme.palette.secondary.main
                                : "transparent",
                              color: isSelected
                                ? "white"
                                : theme.palette.text.primary,
                              border: `1px solid ${isSelected ? theme.palette.secondary.main : theme.palette.divider}`,
                              "&:hover": {
                                bgcolor: isSelected
                                  ? theme.palette.secondary.dark
                                  : alpha(theme.palette.secondary.main, 0.08),
                                borderColor: theme.palette.secondary.main,
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                  </Box>
                );
              })}
            </Box>

            {/* Frequently Bought Together */}
            {(loadingFbt || frequentlyBoughtTogether.length > 0) && (
              <Paper
                elevation={0}
                sx={{
                  flexShrink: 0,
                  p: 2,
                  borderRadius: 2.5,
                  border: `1px solid ${alpha(theme.palette.success.main, 0.22)}`,
                  bgcolor: alpha(theme.palette.success.main, 0.04),
                  position: "sticky",
                  bottom: 0,
                  zIndex: 2,
                  boxShadow: `0 -8px 20px ${alpha(theme.palette.common.black, 0.06)}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.75,
                  }}
                >
                  <AddShoppingCart
                    sx={{ fontSize: 20, color: theme.palette.success.dark }}
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                    Frequently Bought Together
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1.75 }}
                >
                  Customers often pair these with {item.name}
                </Typography>

                {loadingFbt ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                    <CircularProgress size={28} color="success" />
                  </Box>
                ) : (
                  (() => {
                    const PAGE_SIZE = 3;
                    const totalPages = Math.ceil(frequentlyBoughtTogether.length / PAGE_SIZE);
                    const pageItems = frequentlyBoughtTogether.slice(fbtIndex * PAGE_SIZE, fbtIndex * PAGE_SIZE + PAGE_SIZE);
                    return (
                      <Box>
                        {/* Carousel slide — up to 3 cards */}
                        <Box
                          key={fbtIndex}
                          sx={{
                            display: "flex",
                            gap: 1.25,
                            animation: "fbtFadeIn 0.2s ease",
                            "@keyframes fbtFadeIn": {
                              from: { opacity: 0, transform: "translateX(6px)" },
                              to: { opacity: 1, transform: "translateX(0)" },
                            },
                          }}
                        >
                          {pageItems.map((relatedItem) => {
                            const hasVariations = relatedItem.variations && relatedItem.variations.length > 0;
                            const hasModifiers = relatedItem.modifierOptions && relatedItem.modifierOptions.length > 0;
                            const requiresCustomization = hasVariations || hasModifiers;
                            const wasAdded = relatedAddedIds[relatedItem.id];
                            return (
                              <Paper
                                key={relatedItem.id}
                                elevation={0}
                                sx={{
                                  flex: 1,
                                  p: 1.25,
                                  borderRadius: 2,
                                  bgcolor: "white",
                                  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 1,
                                  minWidth: 0,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: 72,
                                    borderRadius: 1.5,
                                    overflow: "hidden",
                                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  {relatedItem.image ? (
                                    <ImageLoader
                                      imagePath={relatedItem.image}
                                      width="100%"
                                      height={72}
                                      alt={relatedItem.name}
                                    />
                                  ) : (
                                    <Typography variant="h6" sx={{ fontWeight: 800, opacity: 0.35 }}>
                                      {relatedItem.name?.charAt(0)}
                                    </Typography>
                                  )}
                                </Box>

                                <Box sx={{ minWidth: 0 }}>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontWeight: 700,
                                      display: "block",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {relatedItem.name}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{ color: theme.palette.primary.main, fontWeight: 700 }}
                                  >
                                    Rs. {getDisplayPrice(relatedItem).toFixed(2)}
                                  </Typography>
                                </Box>

                                <Button
                                  size="small"
                                  onClick={() => handleAddRelatedItem(relatedItem)}
                                  variant={wasAdded ? "outlined" : "contained"}
                                  sx={{
                                    textTransform: "none",
                                    borderRadius: 1.5,
                                    fontWeight: 700,
                                    minHeight: 30,
                                    fontSize: "0.7rem",
                                    px: 0.5,
                                  }}
                                >
                                  {wasAdded ? "Added" : requiresCustomization ? "Customize" : "Quick Add"}
                                </Button>
                              </Paper>
                            );
                          })}
                        </Box>

                        {/* Navigation row — only when more than one page */}
                        {totalPages > 1 && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mt: 1.25 }}>
                            <IconButton
                              size="small"
                              onClick={() => setFbtIndex((i) => Math.max(0, i - 1))}
                              disabled={fbtIndex === 0}
                              sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                                "&:disabled": { opacity: 0.3 },
                                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.22) },
                              }}
                            >
                              <ChevronLeft fontSize="small" />
                            </IconButton>

                            <Box sx={{ display: "flex", gap: 0.75 }}>
                              {Array.from({ length: totalPages }).map((_, idx) => (
                                <Box
                                  key={idx}
                                  onClick={() => setFbtIndex(idx)}
                                  sx={{
                                    width: idx === fbtIndex ? 18 : 7,
                                    height: 7,
                                    borderRadius: 4,
                                    bgcolor: idx === fbtIndex ? theme.palette.success.main : alpha(theme.palette.success.main, 0.3),
                                    cursor: "pointer",
                                    transition: "all 0.25s ease",
                                  }}
                                />
                              ))}
                            </Box>

                            <IconButton
                              size="small"
                              onClick={() => setFbtIndex((i) => Math.min(totalPages - 1, i + 1))}
                              disabled={fbtIndex === totalPages - 1}
                              sx={{
                                bgcolor: alpha(theme.palette.success.main, 0.12),
                                border: `1px solid ${alpha(theme.palette.success.main, 0.25)}`,
                                "&:disabled": { opacity: 0.3 },
                                "&:hover": { bgcolor: alpha(theme.palette.success.main, 0.22) },
                              }}
                            >
                              <ChevronRight fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    );
                  })()
                )}
              </Paper>
            )}
          </Box>

          {/* Right Side - Available Offers */}
          <Box
            sx={{
              width: 420,
              flexShrink: 0,
              bgcolor: alpha(theme.palette.warning.main, 0.03),
              display: "flex",
              flexDirection: "column",
              borderLeft: `2px solid ${alpha(theme.palette.divider, 0.1)}`,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 3,
                p: 3,
                pb: 0,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.warning.main, 0.15),
                  border: `2px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <LocalOffer
                  sx={{ fontSize: 20, color: theme.palette.warning.main }}
                />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, lineHeight: 1.2 }}
                >
                  Special Offers
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontWeight: 600 }}
                >
                  {offers.length} available
                </Typography>
              </Box>
            </Box>

            {/* Carousel body */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", px: 2 }}>
              {loadingOffers ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", flex: 1 }}>
                  <CircularProgress size={40} />
                </Box>
              ) : offers.length === 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flex: 1 }}>
                  <LocalOffer sx={{ fontSize: 48, color: alpha(theme.palette.grey[400], 0.5), mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    No offers available
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
                  {/* Slide area */}
                  <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
                    <Box
                      key={offers[currentOfferIndex]?.id}
                      sx={{
                        height: "100%",
                        overflow: "hidden",
                        animation: "fadeIn 0.25s ease",
                        display: "flex",
                        alignItems: "center",
                        "@keyframes fadeIn": { from: { opacity: 0, transform: "translateY(6px)" }, to: { opacity: 1, transform: "translateY(0)" } },
                      }}
                    >
                      {(() => {
                        const offer = offers[currentOfferIndex];
                        if (!offer) return null;
                        return (
                          <Paper
                            elevation={2}
                            onClick={handleOpenOffersDialog}
                            sx={{
                              width: "100%",
                              borderRadius: 2.5,
                              border: `2px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                              bgcolor: "white",
                              overflow: "hidden",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                boxShadow: `0 8px 24px ${alpha(theme.palette.warning.main, 0.24)}`,
                                transform: "translateY(-2px)",
                              },
                            }}
                          >
                            {offer.image && (
                              <Box sx={{ height: 210, overflow: "hidden" }}>
                                <ImageLoader
                                  imagePath={offer.image}
                                  width="100%"
                                  height={210}
                                  alt={offer.name}
                                />
                              </Box>
                            )}
                            <Box sx={{ p: 1.5 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontWeight: 800,
                                  mb: 0.75,
                                  lineHeight: 1.25,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 1,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {offer.name}
                              </Typography>
                              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                <Chip
                                  label={`Rs. ${parseFloat(offer.price).toFixed(0)}`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.success.main, 0.15),
                                    color: theme.palette.success.dark,
                                  }}
                                />
                                <Chip
                                  label={`${offer.OfferItems?.length || 0} items`}
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                                    color: theme.palette.warning.dark,
                                  }}
                                />
                              </Box>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: "block", fontWeight: 700 }}>
                                Tap card for full offer details
                              </Typography>
                            </Box>
                          </Paper>
                        );
                      })()}
                    </Box>
                  </Box>

                  {/* Navigation row */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, py: 1.5 }}>
                    <IconButton
                      size="small"
                      onClick={() => setCurrentOfferIndex((i) => Math.max(0, i - 1))}
                      disabled={currentOfferIndex === 0}
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                        "&:disabled": { opacity: 0.3 },
                        "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.22) },
                      }}
                    >
                      <ChevronLeft fontSize="small" />
                    </IconButton>

                    {/* Dots */}
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      {offers.map((_, idx) => (
                        <Box
                          key={idx}
                          onClick={() => setCurrentOfferIndex(idx)}
                          sx={{
                            width: idx === currentOfferIndex ? 18 : 7,
                            height: 7,
                            borderRadius: 4,
                            bgcolor: idx === currentOfferIndex ? theme.palette.warning.main : alpha(theme.palette.warning.main, 0.3),
                            cursor: "pointer",
                            transition: "all 0.25s ease",
                          }}
                        />
                      ))}
                    </Box>

                    <IconButton
                      size="small"
                      onClick={() => setCurrentOfferIndex((i) => Math.min(offers.length - 1, i + 1))}
                      disabled={currentOfferIndex === offers.length - 1}
                      sx={{
                        bgcolor: alpha(theme.palette.warning.main, 0.12),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.25)}`,
                        "&:disabled": { opacity: 0.3 },
                        "&:hover": { bgcolor: alpha(theme.palette.warning.main, 0.22) },
                      }}
                    >
                      <ChevronRight fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Action Buttons in Offers Panel */}
            <Box
              sx={{
                borderTop: `2px solid ${alpha(theme.palette.divider, 0.15)}`,
                p: 2.5,
                bgcolor: "white",
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                /* Grow to fill remaining column height so the top of this
                   panel aligns with the top of the FBT card in the middle column */
                flex: 1,
              }}
            >
              {/* Add Another — secondary action */}
              <Button
                variant="outlined"
                onClick={handleAddAnother}
                disabled={!canAddToCart()}
                startIcon={<PlaylistAdd sx={{ fontSize: 22 }} />}
                sx={{
                  flex: 1,
                  fontSize: "1rem",
                  fontWeight: 700,
                  borderRadius: 2,
                  textTransform: "none",
                  letterSpacing: 0.2,
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.35)}`,
                  color: theme.palette.primary.main,
                  bgcolor: alpha(theme.palette.primary.main, 0.04),
                  transition: "all 0.18s ease",
                  "&:hover": {
                    border: `2px solid ${theme.palette.primary.main}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    boxShadow: `0 3px 10px ${alpha(theme.palette.primary.main, 0.2)}`,
                  },
                  "&:disabled": {
                    opacity: 0.4,
                    border: `2px solid ${theme.palette.divider}`,
                  },
                }}
              >
                Add Another
              </Button>

              {/* Add to Bill — primary action */}
              <Button
                variant="contained"
                onClick={handleAddToCart}
                disabled={!canAddToCart()}
                startIcon={<ReceiptLong sx={{ fontSize: 26 }} />}
                sx={{
                  flex: 2,
                  fontSize: "1.15rem",
                  fontWeight: 800,
                  borderRadius: 2,
                  textTransform: "none",
                  letterSpacing: 0.3,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                  transition: "all 0.18s ease",
                  "&:hover": {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0px)",
                    boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.35)}`,
                  },
                  "&:disabled": {
                    opacity: 0.45,
                    background: theme.palette.action.disabledBackground,
                    boxShadow: "none",
                  },
                }}
              >
                <Box sx={{ textAlign: "left" }}>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.85, fontWeight: 600, lineHeight: 1, mb: 0.5 }}
                  >
                    Add to Bill
                  </Typography>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 800, lineHeight: 1 }}
                  >
                    Rs. {calculateTotalPrice().toFixed(2)}
                  </Typography>
                </Box>
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>

      <Dialog
        open={isOffersDialogOpen}
        onClose={() => setIsOffersDialogOpen(false)}
        maxWidth={false}
        sx={customizationOverlaySx}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", bgcolor: "#fafafa" }}>
          <Box
            sx={{
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
              color: "white",
            }}
          >
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800 }}>
                Related Offers
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Detailed offers for {item?.name}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<ShoppingCart />}
                onClick={handleGoToCartFromOffers}
                sx={{
                  minHeight: 40,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  bgcolor: alpha("#fff", 0.18),
                  border: `1px solid ${alpha("#fff", 0.35)}`,
                  color: "white",
                  "&:hover": {
                    bgcolor: alpha("#fff", 0.26),
                  },
                }}
              >
                View Cart ({cartItemCount})
              </Button>
              <IconButton onClick={() => setIsOffersDialogOpen(false)} sx={{ color: "white" }}>
                <Close />
              </IconButton>
            </Box>
          </Box>

          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {offers.length === 0 ? (
              <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 1 }}>
                <LocalOffer sx={{ fontSize: 52, color: alpha(theme.palette.text.secondary, 0.4) }} />
                <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 700 }}>
                  No related offers available
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {offers.map((offer) => (
                  <Grid key={offer.id} size={{ xs: 12, sm: 6, md: 4 }}>
                    <Paper
                      elevation={2}
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        borderRadius: 2.5,
                        overflow: "hidden",
                        border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                      }}
                    >
                      {offer.image && (
                        <Box sx={{ height: 200, overflow: "hidden" }}>
                          <ImageLoader
                            imagePath={offer.image}
                            width="100%"
                            height={200}
                            alt={offer.name}
                          />
                        </Box>
                      )}

                      <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.25, flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.3 }}>
                          {offer.name}
                        </Typography>

                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                          {offer.type && (
                            <Chip
                              size="small"
                              label={offer.type}
                              sx={{
                                bgcolor: alpha(theme.palette.warning.main, 0.16),
                                color: theme.palette.warning.dark,
                                fontWeight: 700,
                              }}
                            />
                          )}
                          <Chip
                            size="small"
                            label={`Rs. ${parseFloat(offer.price).toFixed(2)}`}
                            sx={{
                              bgcolor: alpha(theme.palette.success.main, 0.16),
                              color: theme.palette.success.dark,
                              fontWeight: 700,
                            }}
                          />
                          <Chip
                            size="small"
                            label={`${offer.OfferItems?.length || 0} items`}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: theme.palette.primary.dark,
                              fontWeight: 700,
                            }}
                          />
                        </Box>

                        {offer.description && (
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {offer.description}
                          </Typography>
                        )}

                        {offer.OfferItems?.length > 0 && (
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary }}>
                              Includes
                            </Typography>
                            <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.5 }}>
                              {offer.OfferItems.map((oi) => (
                                <Typography key={oi.id} variant="caption" sx={{ fontWeight: 600 }}>
                                  {oi.MenuItem?.name || "Item"} x {oi.quantity}
                                </Typography>
                              ))}
                            </Box>
                          </Box>
                        )}

                        <Box sx={{ mt: "auto", pt: 1 }}>
                          <Button
                            fullWidth
                            variant="contained"
                            startIcon={<AddShoppingCart />}
                            onClick={() => handleAddOfferFromDialog(offer)}
                            sx={{
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 800,
                              minHeight: 44,
                              background: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                            }}
                          >
                            Add Offer To Cart
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Box>
      </Dialog>
    </Dialog>
  );
};

export default ItemCustomizationDialog;
